'use client';

import React from 'react';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Link } from '@/lib/i18n/navigation';
import { BrandMark } from './BrandMark';

export function Footer() {
  const currentYear = new Date().getFullYear();
  const t = useTranslations('footer');
  const pathname = usePathname();
  const cleanPath = (pathname || '').replace(/^\/(en|hi)/, '') || '/';
  const isReportsActive = cleanPath === '/reports';

  return (
    <footer
      className="relative z-20 w-full max-w-[1160px] mx-auto px-4 sm:px-6 pb-12 mt-12 sm:mt-16 print:hidden text-xs text-[rgba(220,236,255,0.78)]"
      data-testid="app-footer"
    >
      {/* Glass Card Container overlaying lower landscape */}
      <div className="p-8 sm:p-10 rounded-[28px] border border-white/[0.14] bg-[rgba(6,12,38,0.88)] backdrop-blur-xl shadow-[0_20px_50px_rgba(0,0,0,0.7),inset_0_1px_0_rgba(255,255,255,0.25)] space-y-10">
        {/* Main 5-Column Navigation Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Column 1: Brand and Tagline */}
          <div className="lg:col-span-1 space-y-3">
            <Link
              href="/"
              className="inline-block group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] rounded"
            >
              <BrandMark size="md" showWordmark={true} />
            </Link>
            <p className="text-xs text-[rgba(220,236,255,0.78)] leading-relaxed">
              {t('description')}
            </p>
          </div>

          {/* Column 2: Free Tools */}
          <div className="space-y-3">
            <h3 className="text-[11px] font-bold uppercase tracking-wider text-[#b9c6ff]">
              {t('calculatorsHeading')}
            </h3>
            <ul className="space-y-2 text-[rgba(225,238,255,0.85)]">
              <li>
                <Link href="/birth-chart-calculator" className="hover:text-white transition-colors">
                  {t('birthChart')}
                </Link>
              </li>
              <li>
                <Link href="/kundli-matching" className="hover:text-white transition-colors">
                  {t('kundliMatching')}
                </Link>
              </li>
              <li>
                <Link href="/moon-sign-calculator" className="hover:text-white transition-colors">
                  {t('moonSign')}
                </Link>
              </li>
              <li>
                <Link href="/numerology-calculator" className="hover:text-white transition-colors">
                  {t('numerology')}
                </Link>
              </li>
              <li>
                <Link href="/all-calculators" className="text-[#5ab4ff] font-bold hover:underline flex items-center gap-1">
                  <span>{t('allCalculators')}</span>
                  <span>→</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Horoscopes and Panchang */}
          <div className="space-y-3">
            <h3 className="text-[11px] font-bold uppercase tracking-wider text-[#b9c6ff]">
              {t('observationHeading')}
            </h3>
            <ul className="space-y-2 text-[rgba(225,238,255,0.85)]">
              <li>
                <Link href="/horoscope/today-horoscope?period=daily" className="hover:text-white transition-colors">
                  {t('dailyMoonForecast')}
                </Link>
              </li>
              <li>
                <Link href="/horoscope/today-horoscope?period=weekly" className="hover:text-white transition-colors">
                  {t('weeklyTransits')}
                </Link>
              </li>
              <li>
                <Link href="/panchang" className="hover:text-white transition-colors">
                  {t('dailyPanchang')}
                </Link>
              </li>
              <li>
                <Link
                  href="/reports"
                  className={isReportsActive ? 'text-[#5ab4ff] font-semibold' : 'hover:text-white transition-colors'}
                  data-testid="footer-reports-link"
                >
                  {t('reports')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4: Compatibility */}
          <div className="space-y-3">
            <h3 className="text-[11px] font-bold uppercase tracking-wider text-[#b9c6ff]">
              {t('matchingHeading')}
            </h3>
            <ul className="space-y-2 text-[rgba(225,238,255,0.85)]">
              <li>
                <Link href="/compatibility" className="hover:text-white transition-colors">
                  {t('zodiacCompatibility')}
                </Link>
              </li>
              <li>
                <Link href="/kundli-matching" className="hover:text-white transition-colors">
                  {t('kundliMatching')}
                </Link>
              </li>
              <li>
                <Link href="/marriage-compatibility-calculator" className="hover:text-white transition-colors">
                  {t('marriageAnalysis')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 5: Legal and Observatory Log */}
          <div className="space-y-3">
            <h3 className="text-[11px] font-bold uppercase tracking-wider text-[#b9c6ff]">
              {t('observatoryLogHeading')}
            </h3>
            <ul className="space-y-2 text-[rgba(225,238,255,0.85)]">
              <li>
                <Link href="/about" className="hover:text-white transition-colors">
                  {t('about')}
                </Link>
              </li>
              <li>
                <Link href="/contact" className="hover:text-white transition-colors">
                  {t('contact')}
                </Link>
              </li>
              <li>
                <Link href="/support" className="hover:text-white transition-colors">
                  {t('support')}
                </Link>
              </li>
              <li>
                <Link href="/privacy-policy" className="hover:text-white transition-colors">
                  {t('privacy')}
                </Link>
              </li>
              <li>
                <Link href="/terms-of-service" className="hover:text-white transition-colors">
                  {t('terms')}
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar: Copyright and Quiet Promise */}
      <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-[11px] text-[rgba(220,236,255,0.68)] px-2">
        <div className="flex items-center gap-2">
          <BrandMark size="sm" showWordmark={false} markOpacity={0.6} />
          <span>{t('copyright', { year: currentYear })} {t('tagline')}</span>
        </div>
        <div>
          {t('engineCredit')}
        </div>
      </div>
    </footer>
  );
}
